import { useMutation } from "@tanstack/react-query";
import { fetchWorkInfo, WorkInfoStatus } from "@/services/api";

import { vipModalShowAtom } from "@/atom/user";
import { message } from "@/components/Toast";
import Modal from "@/components/Modal";

import {useSetAtom, useAtom, useAtomValue} from "jotai";
import { useNavigate } from "react-router-dom";
import { useErrorHandler } from "@/hooks/useErrorHandler";

import {
  worksAtom,
  workIdAtom,
  workSelectedAtom,
  generateTasksAtom,
  workDataAtom,
} from "@/atom/drawBoard";

import { pushWorkItemTop } from "@/services/Course/courseApi";
import { deleteWork, fetchHomeInit } from "@/services/api";
import type { GenerateTask } from "@/services/api";
import { startGenerateLesson, renameGenerateLesson, deleteGenerateLesson, pinGenerateLesson } from "@/services/generateApi";

const useWork = () => {
  const { handleError } = useErrorHandler();
  const setVipModalShow = useSetAtom(vipModalShowAtom);
  const setWorkId = useSetAtom(workIdAtom);
  const [workSelected, setWorkSelected] = useAtom(workSelectedAtom);
  const setWorks = useSetAtom(worksAtom);
  const setGenerateTasks = useSetAtom(generateTasksAtom);
  const setWorkData = useSetAtom(workDataAtom);
  let navigate = useNavigate();

  const mutationWorkInfo = useMutation({
    mutationFn: fetchWorkInfo,
    onSuccess: (data) => {
      const { workId, status, lessonId } = data?.data || {};
      setWorks((preWorks: []) => [data?.data, ...preWorks]);
      // setWorkId(workId);

      //switch to the work created
      navigate(`/work?workId=${workId}`);
      setWorkSelected(data.data);
      setWorkId(workId);
      const url = new URL(window.location.href);
      url.searchParams.set("workId", workId);
      window.history.replaceState({}, "", url.toString());

      let msg;
      if (data.code !== 0) {
        Modal.show({
          title: `${data.msg || "出错了"}, 请重试`,
          confirmTxt: "确定",
          cancelTxt: "",
        });
        return;
      }
      switch (status) {
        case WorkInfoStatus.NoPermission:
          if (lessonId) {
            msg = "无权限，请先购买课程";
          } else {
            msg = "无权限，请开通或升级会员";
            setVipModalShow(true);
          }
          break;
        case WorkInfoStatus.LessonComplete:
          msg = "用户会员轮次已经用完，请开通更高级别会员继续创作";
          setVipModalShow(true);
          break;

        default:
          break;
      }
      if (msg) {
        message.info(msg);
        return;
      }
      // if (workId) {
      //   // window.open(`/course/?workId=${workId}`);
      //   setWorkId(workId);
      // }
    },
  });

  const add = () => {
    mutationWorkInfo.mutate({});
  };

  // 使用 React Query mutation 管理任务创建
  const mutationGenerateTask = useMutation({
    mutationFn: () => startGenerateLesson(),
    onSuccess: (result) => {
      if (result?.code === 0 && result?.data) {
        const { generateId, name, generateStatus, insertStamp } = result.data;

        // 核心：将新任务添加到 generateTasks 列表开头（参照works模式）
        if (setGenerateTasks) {
          setGenerateTasks((prevTasks: GenerateTask[]) => {
            const newTask: GenerateTask = {
              generateId,
              name: name || '未命名项目',
              generateStatus: generateStatus ?? 0,
              insertStamp: insertStamp || new Date().toISOString(),
            };
            return [newTask, ...prevTasks];
          });
        }

        // 导航到新创建的任务
        navigate(`/work?taskId=${generateId}`);

        // 更新 URL 参数
        const url = new URL(window.location.href);
        url.searchParams.set("taskId", generateId.toString());
        window.history.replaceState({}, "", url.toString());
      } else {
        handleError({
          code: result?.code ?? -1,
          msg: result?.msg ?? '创建任务失败，请重试'
        }, 'CreateGenerateTask');
      }
    },
    onError: (error) => {
      handleError({ code: -1, msg: '创建任务失败' }, 'CreateGenerateTask');
    },
  });

  // 创建 generateTask（课程任务）
  const addGenerateTask = () => {
    mutationGenerateTask.mutate();
  };

  const setWorksList = (works: any) => {
    setWorks(works || []);
  };

  const makeWorkTop = async (work: any, isTop: Boolean) => {
    let result = await pushWorkItemTop(work?.workId, isTop);
    if (result?.code === 0 && result?.msg === "success") {
      setWorksList(result.data.works);
    }
  };

  const removeWork = async (workId: string) => {
    const isTheSelectedWork = workSelected.workId === workId;

    try {
      let result = await deleteWork({ workId: workId });

      if (result?.code === 0 && result?.msg === "success") {
        message.info("删除成功");
        setWorksList(result.data.works);
      } else {
        handleError({
          code: result?.code ?? -1,
          msg: result?.msg ?? '删除失败，请重试'
        }, 'RemoveWork');
      }
    } catch (error) {
      handleError({ code: -1, msg: '删除失败' }, 'RemoveWork');
    }

    if (!isTheSelectedWork) return;

    // 重新获取结果数据
    const result = await deleteWork({ workId: workId });
    if (result.data.works.length > 0) {
      let item = result.data.works[0];
      setWorkSelected(item);
      const url = new URL(window.location.href);
      url.searchParams.set("workId", item.workId);
      window.history.replaceState({}, "", url.toString());
    } else {
      navigate("/");
    }
  };

  // 重命名做课任务
  const renameGenerateTask = async (task: GenerateTask, newName: string): Promise<boolean> => {
    // 验证空名称
    if (!newName || newName.trim() === '') {
      message.info("项目名称不能为空");
      return false;
    }

    try {
      const result = await renameGenerateLesson({
        generateId: task.generateId,
        newName: newName.trim(),
      });

      if (result?.code === 0 && result?.msg === "success") {
        message.info("任务名称修改成功");

        // 更新本地状态
        setGenerateTasks((prevTasks: GenerateTask[]) =>
          prevTasks.map((t) =>
            t.generateId === task.generateId
              ? { ...t, name: newName }
              : t
          )
        );

        // 同步更新workDataAtom
        setWorkData((prevData) => {
          if (!prevData) return prevData;

          // 检查是否是当前任务
          // WorkInfo 有 workId，GenerateTaskInfo 有 generateId
          const currentGenerateId = 'generateId' in prevData ? prevData.generateId : prevData.currentTaskId;
          if (currentGenerateId === task.generateId) {
            return {
              ...prevData,
              workName: newName
            };
          }
          return prevData;
        });

        return true;
      } else {
        message.info(result?.msg || "重命名失败，请重试");
        handleError({
          code: result?.code ?? -1,
          msg: result?.msg ?? '重命名失败，请重试'
        }, 'RenameGenerateTask');
        return false;
      }
    } catch (error) {
      message.info("重命名失败，请重试");
      handleError({ code: -1, msg: '重命名失败' }, 'RenameGenerateTask');
      return false;
    }
  };

  // 置顶/取消置顶做课任务
  const makeGenerateTaskTop = async (task: GenerateTask, isTop: boolean) => {
    try {
      const result = await pinGenerateLesson({
        generateId: task.generateId,
        isPinned: isTop ? 1 : 0,
      });

      if (result?.code === 0 && result?.msg === "success") {
        message.info(isTop ? "已置顶" : "已取消置顶");
        // 从服务器返回的新数组数据更新列表
        if (result.data && Array.isArray(result.data)) {
          setGenerateTasks(result.data);
        }
      } else {
        handleError({
          code: result?.code ?? -1,
          msg: result?.msg ?? '置顶操作失败，请重试'
        }, 'PinGenerateTask');
      }
    } catch (error) {
      handleError({ code: -1, msg: '置顶操作失败' }, 'PinGenerateTask');
    }
  };

  // 删除做课任务
  const removeGenerateTask = async (generateId: number) => {
    const currentUrl = new URL(window.location.href);
    const currentTaskId = currentUrl.searchParams.get("taskId");
    const isTheSelectedTask = currentTaskId === generateId.toString();

    try {
      const result = await deleteGenerateLesson({ generateId });

      if (result?.code === 0 && result?.msg === "success") {
        message.info("删除成功");

        // 使用服务器返回的完整数组更新列表（与 makeGenerateTaskTop 保持一致）
        if (result.data && Array.isArray(result.data)) {
          setGenerateTasks(result.data);
        }

        // 如果删除的是当前选中的任务，处理导航
        if (isTheSelectedTask) {
          const remainingTasks = result.data || [];

          if (remainingTasks.length > 0) {
            // 导航到第一个剩余任务
            const nextTask = remainingTasks[0];
            navigate(`/work?taskId=${nextTask.generateId}`, {
              state: {
                taskData: nextTask,
                source: 'controlPanel'
              }
            });
          } else {
            // 没有剩余任务，导航到首页
            navigate("/");
          }
        }
      } else {
        handleError({
          code: result?.code ?? -1,
          msg: result?.msg ?? '删除失败，请重试'
        }, 'DeleteGenerateTask');
      }
    } catch (error) {
      handleError({ code: -1, msg: '删除失败' }, 'DeleteGenerateTask');
    }
  };

  return {
    add,
    addGenerateTask,
    removeWork,
    makeWorkTop,
    setWorksList,
    renameGenerateTask,
    makeGenerateTaskTop,
    removeGenerateTask,
  };
};

export default useWork;
